import React from 'react';
import type { StageBasicDetails, StageApprover } from './types';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MOCK_USERS } from './constants';
import { User, Users, Briefcase, UserCheck, Edit3 } from 'lucide-react';
import {
  ApprovalCard,
  ApprovalCardHeader,
  ApprovalCardTitle,
  ApprovalCardDescription,
  ApprovalCardContent,
} from '../shared/approval';

interface Step1BasicDetailsProps {
  basicDetails: StageBasicDetails;
  approver: StageApprover;
  allowApproverEdit?: boolean;
  onBasicDetailsChange: (data: StageBasicDetails) => void;
  onApproverChange: (data: StageApprover) => void;
  onAllowApproverEditChange?: (value: boolean) => void;
}

export const Step1BasicDetails: React.FC<Step1BasicDetailsProps> = ({
  basicDetails,
  approver,
  allowApproverEdit = false,
  onBasicDetailsChange,
  onApproverChange,
  onAllowApproverEditChange,
}) => {
  const [errors, setErrors] = React.useState<{ stageName?: string; specificUserId?: string }>({});


  const approverTypes = [
    {
      id: 'DirectManager',
      icon: User,
      title: 'Direct Manager',
      description: "Employee's line manager",
    },
    {
      id: 'ProjectManager',
      icon: Briefcase,
      title: 'Project Manager',
      description: 'Manager of the project',
    },
    {
      id: 'DepartmentHead',
      icon: Users,
      title: 'Department Head',
      description: 'Head of department',
    },
    {
      id: 'SpecificPerson',
      icon: UserCheck,
      title: 'Specific Person',
      description: 'Choose a specific approver',
    },
  ];

  const handleStageNameChange = (value: string) => {
    onBasicDetailsChange({ ...basicDetails, stageName: value });
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, stageName: undefined }));
    }
  };

  const handleStageNameBlur = () => {
    if (!basicDetails.stageName.trim()) {
      setErrors((prev) => ({ ...prev, stageName: 'Stage name is required' }));
    }
  };

  const handleApproverTypeSelect = (typeId: string) => {
    if (typeId === 'SpecificPerson') {
      onApproverChange({
        ...approver,
        approverType: 'user',
        roleType: undefined,
      });
    } else {
      onApproverChange({
        ...approver,
        approverType: 'role',
        roleType: typeId as 'DirectManager' | 'ProjectManager' | 'DepartmentHead',
        specificUserId: undefined,
      });
    }
  };

  const getSelectedType = () => {
    if (approver.approverType === 'user') return 'SpecificPerson';
    return approver.roleType || '';
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Compact Header */}
      <div className="flex-shrink-0 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Stage Details & Approver</h2>
          <p className="text-sm text-muted-foreground">Configure stage name and approver</p>
        </div>
      </div>

      {/* Scrollable Content - Two Column Layout */}
      <div className="flex-1 overflow-y-auto pr-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column: Stage Details */}
          <ApprovalCard variant="elevated" className="h-fit">
            <ApprovalCardHeader className="pb-3">
              <ApprovalCardTitle className="text-base">Stage Information</ApprovalCardTitle>
              <ApprovalCardDescription className="text-xs">
                Basic details about this approval stage
              </ApprovalCardDescription>
            </ApprovalCardHeader>
            <ApprovalCardContent className="space-y-3 pt-0">
              {/* Stage Name */}
              <div className="space-y-1.5">
                <Label htmlFor="stageName" className="text-foreground text-sm">
                  Stage Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="stageName"
                  placeholder="e.g., Line Manager Approval"
                  value={basicDetails.stageName}
                  onChange={(e) => handleStageNameChange(e.target.value)}
                  onBlur={handleStageNameBlur}
                  className={`h-9 text-sm ${errors.stageName ? 'border-red-500' : ''}`}
                />
                {errors.stageName && <p className="text-xs text-destructive">{errors.stageName}</p>}
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="description" className="text-foreground text-sm">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the purpose of this approval stage..."
                  value={basicDetails.description || ''}
                  onChange={(e) =>
                    onBasicDetailsChange({ ...basicDetails, description: e.target.value })
                  }
                  rows={3}
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Provide additional context about this stage
                </p>
              </div>
            </ApprovalCardContent>
          </ApprovalCard>

          {/* Right Column: Approver Selection */}
          <ApprovalCard variant="elevated" className="h-fit">
            <ApprovalCardHeader className="pb-3">
              <ApprovalCardTitle className="text-base">
                Approver Selection <span className="text-destructive">*</span>
              </ApprovalCardTitle>
              <ApprovalCardDescription className="text-xs">
                Choose who should approve at this stage
              </ApprovalCardDescription>
            </ApprovalCardHeader>
            <ApprovalCardContent className="space-y-4 pt-0">
              {/* Approver Type Cards - 2x2 Grid */}
              <div className="grid grid-cols-2 gap-2">
                {approverTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = getSelectedType() === type.id;

                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleApproverTypeSelect(type.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        isSelected
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-accent hover:bg-accent'
                      }`}
                    >
                      <Icon
                        className={`h-6 w-6 mx-auto mb-1.5 ${
                          isSelected ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      />
                      <div className={`font-semibold text-xs mb-0.5 ${
                        isSelected ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {type.title}
                      </div>
                      <div className="text-[10px] text-muted-foreground line-clamp-1">
                        {type.description}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Specific User Selection */}
              {approver.approverType === 'user' && (
                <div className="space-y-1.5">
                  <Label htmlFor="specificUser" className="text-foreground text-sm">Select User</Label>
                  <Select
                    value={approver.specificUserId || ''}
                    onValueChange={(value) =>
                      onApproverChange({ ...approver, specificUserId: value })
                    }
                  >
                    <SelectTrigger 
                      id="specificUser"
                      className={`h-9 text-sm ${errors.specificUserId ? 'border-red-500' : ''}`}
                    >
                      <SelectValue placeholder="Select a user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {MOCK_USERS.map((user) => (
                        <SelectItem key={user.id} value={user.id} className="text-sm">
                          {user.name} - {user.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.specificUserId && (
                    <p className="text-xs text-destructive">{errors.specificUserId}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Select a specific user to approve at this stage
                  </p>
                </div>
              )}

              {/* Cannot Approve Own */}
              <div className="flex items-start space-x-2 pt-1">
                <Checkbox
                  id="cannotApproveOwn"
                  checked={approver.cannotApproveOwn}
                  onCheckedChange={(checked) =>
                    onApproverChange({
                      ...approver,
                      cannotApproveOwn: checked as boolean,
                    })
                  }
                  className="mt-0.5"
                />
                <div className="space-y-0.5 leading-none">
                  <Label
                    htmlFor="cannotApproveOwn"
                    className="text-xs font-medium cursor-pointer text-foreground"
                  >
                    Cannot approve own timesheet
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Prevent self-approval for this stage
                  </p>
                </div>
              </div>

              {/* Allow Approver to Edit */}
              <div className="flex items-start space-x-2 pt-1 border-t border-border mt-3 pt-3">
                <Checkbox
                  id="allowApproverEdit"
                  checked={allowApproverEdit}
                  onCheckedChange={(checked) =>
                    onAllowApproverEditChange?.(checked as boolean)
                  }
                  className="mt-0.5"
                />
                <div className="space-y-0.5 leading-none">
                  <div className="flex items-center space-x-1.5">
                    <Edit3 className="w-3.5 h-3.5 text-primary" />
                    <Label
                      htmlFor="allowApproverEdit"
                      className="text-xs font-medium cursor-pointer text-foreground"
                    >
                      Allow approver to edit timesheet
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Approver can modify entries before approving/rejecting. Edits are tracked.
                  </p>
                  <div className="mt-1.5 p-1.5 bg-accent-info-50 border border-accent-info-200 rounded text-[10px] text-accent-info-700">
                    <strong>Note:</strong> Changes are preserved if rejected.
                  </div>
                </div>
              </div>
            </ApprovalCardContent>
          </ApprovalCard>
        </div>
      </div>
    </div>
  );
};
